import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useThemeStore } from '../../state/theme';
import { fetchAgentList } from '../../hooks/queries';
import { addAgentBrand, updateBrand } from '../../hooks/mutations';
import {
  DAYS_OF_WEEK,
  allTimezones,
  currencyOptions,
  bonusFrequencyOptions,
  NUMERIC_DAYS_OF_WEEK,
  SLACK_ALERT_CHANNELS,
} from '../../constants';
import { IAgent, IAgentBrand } from '../../types/agent';
import { IBrand } from '../../types/brand';
import { agentBrandSchema } from '../../validations/agent';
import toast from 'react-hot-toast';
import { calculateWorkingHours } from '../../utils';
import { ClipboardCopy, Loader2, Minus, Plus, X } from 'lucide-react';
import Select from '../common/Select';
import { BtnDefault, BtnPrimary } from '../common/Buttons';
import PasswordInput from '../common/PasswordInput';
import Switch from '../common/Switch';
interface OnboardAgentProps {
  initialAgents: IAgent[];
  selectedBrand: IBrand | null;
  onSuccess: () => void;
  onClose: () => void;
}

type FormData = IAgentBrand;

const OnboardAgent: React.FC<OnboardAgentProps> = ({
  selectedBrand,
  onSuccess,
}) => {
  const { isDarkMode } = useThemeStore();
  const { data: agentsData } = fetchAgentList();
  const [step, setStep] = useState<'agent' | 'config' | 'schedule'>('agent');
  const [selectedAgent, setSelectedAgent] = useState<IAgent | null>(null);

  const { mutate: updateBrandMutation } = updateBrand();
  const { mutate: addAgentBrandMutation, isPending } = addAgentBrand({
    onSuccess: () => {
      handleBrandUpdate();
      toast.success('Agent onboarded successfully!');
      onSuccess();
    },
    onError: (error) => {
      const e = error as Error;
      toast.error(e.message || 'Failed to onboard agent');
    },
  });

  const formMethods = useForm<FormData>({
    mode: 'onChange',
    resolver: zodResolver(agentBrandSchema),
    defaultValues: {
      brandId: selectedBrand?._id || '',
      agentId: selectedAgent?._id || '',
      email: '',
      password: '',
      onboardingDate: new Date().toISOString().split('T')[0],
      upworkContractId: '',
      upworkFreelancerId: '',
      isUpwork: false,
      payment: [
        {
          toAgent: { currency: 'USD', payType: 'hourly' },
          fromClient: { currency: 'USD', payType: 'hourly' },
          effectiveStartDate: new Date().toISOString().split('T')[0],
          effectiveEndDate: undefined,
        },
      ],
      bonus: { name: '', amount: 0, frequency: 'monthly', dayOfWeek: undefined },
      workSchedule: {
        timeSlots: {},
        breakDuration: { value: 0, unit: 'minutes' },
        tolerancePeriod: selectedBrand?.configuration?.tolerancePeriod ?? 15,
      },
      trialPeriod: { days: 7, startDate: '', status: 'active' },
      timezone: 'Asia/Manila',
      captureTimeInterval: 2,
      overdueSessionThresholdMinutes: 15,
      idleTimeThresholdMinutes: 10,
      enableLeaveFeature: false,
      slackAlertChannels: [],
    },
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
    getValues,
    trigger,
  } = formMethods;

  const timeSlots = watch('workSchedule.timeSlots') || {};

  const inputClass = `w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-blue-500 ${
    isDarkMode
      ? 'bg-gray-700 border-gray-600 text-white'
      : 'bg-white border-greyFifty'
  }`;

  const labelClass = `block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  const handleAgentSelect = (agent: IAgent) => {
    setSelectedAgent(agent);
    setValue('agentId', agent._id || '');
  };


  const toggleWorkingDay = (day: string) => {
    const formattedDay = day.charAt(0).toUpperCase() + day.slice(1, 3);
    const dayShifts = timeSlots[formattedDay];
    const isWorking =
      dayShifts && Array.isArray(dayShifts) && dayShifts.length > 0;

    if (isWorking) {
      setValue(`workSchedule.timeSlots.${formattedDay}` as any, undefined);
    } else {
      const startTime = '09:00';
      const endTime = '17:00';
      setValue(`workSchedule.timeSlots.${formattedDay}` as any, [
        {
          startTime,
          endTime,
          workingHours: calculateWorkingHours(startTime, endTime),
        },
      ]);
    }
  };

  const updateTimeSlot = (
    day: string,
    shiftIndex: number,
    field: 'startTime' | 'endTime',
    value: string
  ) => {
    const formattedDay = day.charAt(0).toUpperCase() + day.slice(1, 3);
    const dayShifts = timeSlots[formattedDay] || [];

    const updatedShifts = [...dayShifts];
    const currentShift = updatedShifts[shiftIndex] || {};

    const updatedShift = {
      ...currentShift,
      [field]: value,
    };

    // Calculate working hours
    if (updatedShift.startTime && updatedShift.endTime) {
      updatedShift.workingHours = calculateWorkingHours(
        updatedShift.startTime,
        updatedShift.endTime
      );
    }

    updatedShifts[shiftIndex] = updatedShift;
    setValue(`workSchedule.timeSlots.${formattedDay}` as any, updatedShifts);
  };

  const copyPreviousDay = (currentDayIndex: number) => {
    if (currentDayIndex === 0) return;

    const currentDay = DAYS_OF_WEEK[currentDayIndex].value;
    const previousDay = DAYS_OF_WEEK[currentDayIndex - 1].value;

    const formattedCurrentDay =
      currentDay.charAt(0).toUpperCase() + currentDay.slice(1, 3);
    const formattedPreviousDay =
      previousDay.charAt(0).toUpperCase() + previousDay.slice(1, 3);

    const previousDayShifts = timeSlots[formattedPreviousDay];

    if (
      previousDayShifts &&
      Array.isArray(previousDayShifts) &&
      previousDayShifts.length > 0
    ) {
      const copiedShifts = previousDayShifts.map(({ _id, ...rest }) => rest);
      setValue(
        `workSchedule.timeSlots.${formattedCurrentDay}` as any,
        copiedShifts
      );
    }
  };

  const addShiftToDay = (day: string) => {
    const formattedDay = day.charAt(0).toUpperCase() + day.slice(1, 3);
    const dayShifts = timeSlots[formattedDay] || [];

    const startTime = '09:00';
    const endTime = '17:00';

    setValue(`workSchedule.timeSlots.${formattedDay}` as any, [
      ...dayShifts,
      {
        startTime,
        endTime,
        workingHours: calculateWorkingHours(startTime, endTime),
      },
    ]);
  };

  const removeShiftFromDay = (day: string, shiftIndex: number) => {
    const formattedDay = day.charAt(0).toUpperCase() + day.slice(1, 3);
    const dayShifts = timeSlots[formattedDay] || [];

    const updatedShifts = dayShifts.filter((_, index) => index !== shiftIndex);

    if (updatedShifts.length === 0) {
      setValue(`workSchedule.timeSlots.${formattedDay}` as any, undefined);
    } else {
      setValue(`workSchedule.timeSlots.${formattedDay}` as any, updatedShifts);
    }
  };

  const handleBrandUpdate = () => {
    if (!selectedBrand || !selectedAgent) return;

    const updatedAgents = [
      ...(selectedBrand?.agents || []),
      selectedAgent?.kimId,
    ].filter((agent): agent is string => agent !== undefined);

    const data: IBrand = {
      _id: selectedBrand._id,
      clientId: selectedBrand.clientId,
      name: selectedBrand.name,
      websiteLink: selectedBrand.websiteLink,
      agents: updatedAgents,
      // alias: selectedBrand.alias,
    };

    updateBrandMutation(data);
  };

  const onSubmit = (data: FormData) => {
    const bonus = (data as any).bonus;
    const payload = {
      ...data,
      bonus: Array.isArray(bonus) ? bonus : bonus ? [bonus] : [],
    };
    addAgentBrandMutation(payload as IAgentBrand);
  };

  const hasShift = Object.values(timeSlots).some((slots: any) => slots?.length > 0);
  const payType = watch('payment.0.toAgent.payType');
  const isDisabled = hasShift || (watch('isUpwork') && payType === 'hourly');

  const renderAgentStep = () => (
    <div className="mx-auto">
      <div className={`rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <h2
          className={`text-base font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
        >
          Onboard Agent for {selectedBrand?.name}
        </h2>

        <div className="space-y-4">
          <div className='space-y-3'>
            <label className={labelClass}>Select Agent</label>
            <Select
              isMulti={false}
              options={
                agentsData?.agents?.map((agent) => ({
                  label: agent.name,
                  value: agent._id || '',
                })) || []
              }
              value={
                selectedAgent
                  ? {
                      label: selectedAgent.name,
                      value: selectedAgent._id || '',
                    }
                  : null
              }
              onChange={(newValue) => {
                if (newValue && !Array.isArray(newValue)) {
                  const agent = agentsData?.agents?.find(
                    (a) => a._id === newValue.value
                  );
                  if (agent) handleAgentSelect(agent);
                }
              }}
              placeholder="Choose an agent..."
            />

            <label className={labelClass}>Onboarding Date</label>
            <input
              {...register('onboardingDate')}
              type="date"
              className={inputClass}
            />

            <div className="flex items-center gap-2">
              <label className={labelClass}>Upwork Agent</label>
              <input type="checkbox" {...register('isUpwork')} />
            </div>

            {watch('isUpwork') ? (
              <>
                <label className={labelClass}>
                  Upwork Contract ID <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('upworkContractId')}
                  type="text"
                  className={inputClass}
                />
                {errors.upworkContractId && (
                  <p className="text-red-500 text-sm">
                    {errors.upworkContractId.message}
                  </p>
                )}

                <label className={labelClass}>
                  Upwork Freelancer ID <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('upworkFreelancerId')}
                  type="text"
                  className={inputClass}
                />
                {errors.upworkFreelancerId && (
                  <p className="text-red-500 text-sm">
                    {errors.upworkFreelancerId.message}
                  </p>
                )}
              </>
            ) : (
              <></>
            )}
          </div>

          {selectedAgent && (
            <div
              className={`p-4 rounded border ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-greyFifty bg-greyTen'}`}
            >
              <h3
                className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
              >
                {selectedAgent.name}
              </h3>
              <p
                className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}
              >
                {selectedAgent.personalEmail}
              </p>
              <span
                className={`inline-block px-2 py-1 text-xs rounded mt-2 ${
                  selectedAgent.status === 'active'
                    ? 'bg-green-100 text-green-800 border'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {selectedAgent.status}
              </span>

              <button
                type="button"
                onClick={async () => {
                  const valid = await trigger(['upworkContractId', 'upworkFreelancerId']);
                  if (valid) setStep('config');
                }}
                className="mt-4 w-full px-4 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-500"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (step === 'agent') {
    return renderAgentStep();
  }

  if (step === 'config') {
    return (
      <div className="mx-auto">
        <div
          className={`py-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} space-y-6`}
        >
          <div className="flex items-center justify-between">
            <h2
              className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
            >
              Configure {selectedAgent?.name}
            </h2>
            <button
              type="button"
              onClick={() => setStep('agent')}
              className="text-blue-700 hover:text-blue-500 text-sm"
            >
              Change Agent
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>
                Email <span className="text-red-500 text-sm">*</span>
              </label>
              <input
                {...register('email')}
                type="email"
                className={inputClass}
                placeholder="agent@example.com"
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.email.message}
                </p>
              )}
            </div>
            <PasswordInput
              name="password"
              formObj={formMethods}
              showPasswordOption={true}
              label="Password"
              validation={{
                required: 'Password is required',
              }}
            />
            <div>
              <label className={labelClass}>Timezone</label>
              <Controller
                name="timezone"
                control={control}
                render={({ field }) => (
                  <select {...field} className={inputClass}>
                    {allTimezones.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
            <div>
              <label className={labelClass}>
                Time Capture Interval (minutes)
              </label>
              <input
                {...register('captureTimeInterval', {
                  setValueAs: (value) => (value === '' ? 2 : Number(value)),
                })}
                type="number"
                step="1"
                min="1"
                className={inputClass}
                placeholder="2"
              />
            </div>
            <div>
              <label className={labelClass}>
                Overdue Session Threshold (minutes)
              </label>
              <input
                {...register('overdueSessionThresholdMinutes', {
                  setValueAs: (value) => (value === '' ? 15 : Number(value)),
                })}
                type="number"
                step="1"
                min="1"
                className={inputClass}
                placeholder="15"
              />
            </div>
            <div>
              <label className={labelClass}>
                Idle Time Threshold (minutes)
              </label>
              <input
                {...register('idleTimeThresholdMinutes', {
                  setValueAs: (value) => (value === '' ? 10 : Number(value)),
                })}
                type="number"
                step="1"
                min="1"
                className={inputClass}
                placeholder="10"
              />
            </div>

            <div className="col-span-2">
              <label className={labelClass}>Slack Alert Channels</label>
              <Controller
                name="slackAlertChannels"
                control={control}
                render={({ field }) => (
                  <Select
                    isMulti
                    options={SLACK_ALERT_CHANNELS}
                    value={SLACK_ALERT_CHANNELS.filter(opt =>
                      (field.value || []).includes(opt.value)
                    )}
                    onChange={(selected: any) =>
                      field.onChange((selected || []).map((o: any) => o.value))
                    }
                    placeholder="Select channels..."
                  />
                )}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className={labelClass}>Enable Leave Feature</label>
                <p
                  className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  Initializes leave balance from brand policy on save
                </p>
              </div>
              <Controller
                name="enableLeaveFeature"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value || false}
                    onChange={(checked) => field.onChange(checked)}
                  />
                )}
              />
            </div>
          </div>

          <div className="space-y-6">
            <h3
              className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
            >
              Payment Configuration
            </h3>

            <div>
              <h4
                className={`text-md font-medium mb-3 ${isDarkMode ? 'text-green-400' : 'text-blackTen'}`}
              >
                Payment to Agent
              </h4>
              <div
                className={`p-4 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'}`}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={labelClass}>Currency</label>
                    <Controller
                      name="payment.0.toAgent.currency"
                      control={control}
                      render={({ field }) => (
                        <select {...field} className={inputClass}>
                          {currencyOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Pay Type</label>
                    <Controller
                      name="payment.0.toAgent.payType"
                      control={control}
                      render={({ field }) => (
                        <select {...field} className={inputClass}>
                          <option value="hourly">Hourly</option>
                          <option value="fixed">Fixed</option>
                          <option value="ticket_based">Ticket-based</option>
                        </select>
                      )}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Amount</label>
                    <input
                      {...register('payment.0.toAgent.amount', {
                        setValueAs: (value) =>
                          value === '' ? undefined : Number(value),
                      })}
                      type="number"
                      step="0.01"
                      min="0"
                      className={inputClass}
                      placeholder="00"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4
                className={`text-md font-medium mb-3 ${isDarkMode ? 'text-greyTen' : 'text-blackTen'}`}
              >
                Payment from Client
              </h4>
              <div
                className={`p-4 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'}`}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={labelClass}>Currency</label>
                    <Controller
                      name="payment.0.fromClient.currency"
                      control={control}
                      render={({ field }) => (
                        <select {...field} className={inputClass}>
                          {currencyOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Pay Type</label>
                    <Controller
                      name="payment.0.fromClient.payType"
                      control={control}
                      render={({ field }) => (
                        <select {...field} className={inputClass}>
                          <option value="hourly">Hourly</option>
                          <option value="fixed">Fixed</option>
                          <option value="ticket_based">Ticket-based</option>
                        </select>
                      )}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Amount</label>
                    <input
                      {...register('payment.0.fromClient.amount', {
                        setValueAs: (value) =>
                          value === '' ? undefined : Number(value),
                      })}
                      type="number"
                      step="0.01"
                      min="0"
                      className={inputClass}
                      placeholder="00"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3
              className={`text-lg font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
            >
              Bonus (Optional)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Bonus Name</label>
                <input
                  {...register('bonus.name')}
                  className={inputClass}
                  placeholder="Performance"
                />
              </div>
              <div>
                <label className={labelClass}>Amount</label>
                <input
                  {...register('bonus.amount', {
                    setValueAs: (value) => (value === '' ? 0 : Number(value)),
                  })}
                  type="number"
                  step="1"
                  min="0"
                  className={inputClass}
                  placeholder="0"
                />
              </div>
              <div>
                <label className={labelClass}>Frequency</label>
                <Controller
                  name="bonus.frequency"
                  control={control}
                  render={({ field }) => (
                    <select {...field} className={inputClass}>
                      {bonusFrequencyOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>
              {
                watch('bonus.frequency') === 'specific_day_of_week' ? (
                <div>
                  <label className={labelClass}>Day of the Week</label>
                  <Controller
                    name="bonus.dayOfWeek"
                    control={control}
                    render={({ field }) => (
                      <select
                        {...field}
                        className={inputClass}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        defaultValue={NUMERIC_DAYS_OF_WEEK[0].value}
                      >
                        {NUMERIC_DAYS_OF_WEEK.map((day) => (
                          <option key={day.value} value={day.value}>
                            {day.label}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </div>
                ) : <></>
              }
              {
                watch('bonus.frequency') === 'specific_day_of_month' ? (
                <div>
                  <label className={labelClass}>Day of the Month</label>
                  <Controller
                    name="bonus.dayOfMonth"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="number"
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        value={field.value}
                        min={1}
                        max={31}
                        defaultValue={10}
                      />
                    )}
                  />
                </div>
                ) : <></>
              }
            </div>
          </div>

          <div className="flex justify-between pt-6 border-t border-gray-200">
            <BtnDefault
              type="button"
              onClick={() => setStep('agent')}
              className="py-2"
            >
              Back
            </BtnDefault>
            <BtnPrimary
              type="button"
              onClick={() => {
                const values = getValues();
                if (!values.email || !values.password) {
                  toast.error('Email and password are required');
                  return;
                }
                setStep('schedule');
              }}
              className="py-2"
            >
              Continue
            </BtnPrimary>
          </div>
        </div>
      </div>
    );
  }

  // Schedule step
  return (
    <div className="mx-auto">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div
          className={`py-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} space-y-6`}
        >
          <div className="flex items-center justify-between">
            <h2
              className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
            >
              Work Schedule & Trial
            </h2>
          </div>

          {/* Schedule */}
          <div>
            <div className="space-y-4">
              {DAYS_OF_WEEK.map((day, dayIndex) => {
                const formattedDay =
                  day.value.charAt(0).toUpperCase() + day.value.slice(1, 3);
                const dayShifts = timeSlots[formattedDay];
                const isWorking =
                  dayShifts && Array.isArray(dayShifts) && dayShifts.length > 0;

                return (
                  <div key={day.value}>
                    <div className="flex items-center gap-4 mb-2">
                      <div className="w-16 text-sm font-medium">
                        {day.label}
                      </div>
                      {!isWorking ? (
                        <button
                          type="button"
                          onClick={() => toggleWorkingDay(day.value)}
                          className="text-blue-500 hover:text-blue-700 text-sm flex items-center gap-1"
                        >
                          <Plus size={16} />
                          <span>Add shift</span>
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => addShiftToDay(day.value)}
                            className="text-blue-500 hover:text-blue-700 text-sm flex items-center gap-1"
                          >
                            <Plus size={16} />
                            <span>Add shift</span>
                          </button>
                          {dayIndex > 0 && (
                            <button
                              type="button"
                              onClick={() => copyPreviousDay(dayIndex)}
                              className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1"
                              title="Copy previous day"
                            >
                              <ClipboardCopy size={16} />
                              <span>Copy prev</span>
                            </button>
                          )}
                        </>
                      )}
                    </div>

                    {isWorking && (
                      <div className="ml-20 space-y-2">
                        {dayShifts.map((shift, shiftIndex) => (
                          <div
                            key={shiftIndex}
                            className="flex items-center gap-2"
                          >
                            <span className="text-xs text-gray-500 w-12">
                              Shift {shiftIndex + 1}
                            </span>
                            <input
                              type="time"
                              value={shift.startTime || ''}
                              onChange={(e) =>
                                updateTimeSlot(
                                  day.value,
                                  shiftIndex,
                                  'startTime',
                                  e.target.value
                                )
                              }
                              className={`${inputClass} w-32 !p-1.5`}
                            />
                            <span>
                              <Minus size={16} />
                            </span>
                            <input
                              type="time"
                              value={shift.endTime || ''}
                              onChange={(e) =>
                                updateTimeSlot(
                                  day.value,
                                  shiftIndex,
                                  'endTime',
                                  e.target.value
                                )
                              }
                              className={`${inputClass} w-32 !p-1.5`}
                            />
                            <span className="text-xs text-gray-500">
                              ({shift.workingHours || 0}h)
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                removeShiftFromDay(day.value, shiftIndex)
                              }
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Break Duration</label>
                <input
                  {...register('workSchedule.breakDuration.value', {
                    setValueAs: (value) => (value === '' ? 0 : Number(value)),
                  })}
                  type="number"
                  step="1"
                  min="0"
                  className={inputClass}
                  placeholder="30 (optional)"
                />
              </div>
              <div>
                <label className={labelClass}>Unit</label>
                <Controller
                  name="workSchedule.breakDuration.unit"
                  control={control}
                  render={({ field }) => (
                    <select {...field} className={inputClass}>
                      <option value="minutes">Minutes</option>
                      <option value="hours">Hours</option>
                    </select>
                  )}
                />
              </div>
              <div className="col-span-2">
                <label className={labelClass}>Monthly Expected Work Hours</label>
                <input
                  {...register('monthlyExpectedWorkHours', {
                    setValueAs: (value) => (value === '' ? undefined : Number(value)),
                  })}
                  type="number"
                  step="1"
                  min="1"
                  required={!isDisabled}
                  disabled={isDisabled}
                  className={`${inputClass} disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100`}
                  placeholder={isDisabled ? 'Derived from shift schedule' : 'e.g. 160'}
                />
              </div>
            </div>
          </div>

          {/* Trial */}
          <div>
            <h3
              className={`text-lg font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
            >
              Trial Period
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Trial Days</label>
                <input
                  {...register('trialPeriod.days', {
                    setValueAs: (value) =>
                      value === '' ? undefined : Number(value),
                  })}
                  type="number"
                  step="1"
                  min="1"
                  className={inputClass}
                  placeholder="7"
                />
              </div>
              <div>
                <label className={labelClass}>Start Date</label>
                <input
                  {...register('trialPeriod.startDate')}
                  type="date"
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-between pt-6 border-t border-gray-200">
            <BtnDefault
              type="button"
              onClick={() => setStep('config')}
              className="py-2"
            >
              Back
            </BtnDefault>
            <BtnPrimary type="submit" disabled={isPending} className="py-2">
              {isPending ? (
                <div className="flex items-center gap-2">
                  <span>Deploying...</span>
                  <Loader2 size={16} className="animate-spin" />
                </div>
              ) : (
                'Deploy'
              )}
            </BtnPrimary>
          </div>
        </div>
      </form>
    </div>
  );
};

export default OnboardAgent;
